import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { Form, message } from 'antd'
import { useRequest } from 'ahooks'
import classNames from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import styled from '@emotion/styled'
import { Modal, Button, Input, IconFont } from '@/components'
import type { ParagraphItem } from '../ParagraphListItem'
import { addSplitContent, updateSplitContent } from '@/apis/datastore/api'
import type { Paragraph } from '@/pages/datastores/types/paragraph'
import KeywordsEditor from '@/pages/datastores/components/KeywordsEditor.tsx'
import { useOSSClient } from '@/hooks/useOSSClient'
import { MAX_SPLIT_SIZE } from '@/features/datastore/constant'
import AnswerEditor from './components/AnswerEditor'

export function RequireIcon() {
  return (
    <span className='color-error ml-5 text-[18px] line-height-14px position-relative top-[3px]'>
      *
    </span>
  )
}

const MAX_ANSWER_COUNT = 4000

const MAX_QUESTION_CONTENT_COUNT = 255

export interface QAParagraphItemEditorModalProps {
  title?: string | React.ReactElement
  defaultValue?: string[]
  open?: boolean
  onCancel?: () => void
  onFinish?: () => void
  paragraph: Omit<ParagraphItem, 'key'> & {
    key?: ParagraphItem['key']
  }
  file_id: number
  chunk_id?: number
  insert_above_chunk_id?: number
  callback: (value: Paragraph) => void
  matched_keywords?: string[]
  operationType: 'update' | 'add'
}

const FormWrapper = styled(Form)`
  .ant-form-item-required {
    ::before {
      display: none !important;
    }
  }
`

function checkArrayItems(
  arr: {
    title: string
    value: string
  }[],
) {
  const keyMap: Record<string, any> = {} // 用于存储key值，检测重复
  for (let i = 0; i < arr.length; i++) {
    const item = arr[i]
    // 检测key是否重复
    if (keyMap[item.title]) {
      return { success: false, message: `重复的标题: ${item.title}` }
    }
    // 检测content是否为空
    if (!item.value) {
      return {
        success: false,
        message: `当前内容不能为空: ${item.title}`,
      }
    }
    if (item.value.length > MAX_SPLIT_SIZE) {
      return {
        success: false,
        message: `当前内容超出最大限制: ${item.title}`,
      }
    }
    // 更新keyMap，记录当前的key
    keyMap[item.title] = true
  }
  // 如果所有检测都通过，则返回成功
  return { success: true, message: 'success' }
}

function QuestionItem(props: {
  value?: any
  onChange?: () => void
  readOnly: boolean
  max: number
}) {
  const { value, onChange, readOnly, max } = props
  return readOnly ? (
    <div
      className={classNames(
        ' rounded-[8px] h-[36px] text-[14px] c-[#17171D] line-height-22px flex items-center px-[12px]',
        {
          'bg-[rgba(98,105,153,0.06)]': !readOnly,
          '!pl-0px': readOnly,
        },
      )}
    >
      <span className='text-nowrap overflow-hidden'>{value}</span>
    </div>
  ) : (
    <Input
      onChange={onChange}
      placeholder='请输入问题'
      value={value}
      count={{
        show: true,
        max,
      }}
    />
  )
}

export const QAParagraphItemEditorModal: FC<
  QAParagraphItemEditorModalProps
> = props => {
  const {
    open,
    onCancel: onModalClose,
    title = '编辑',
    paragraph,
    file_id,
    chunk_id,
    callback,
    matched_keywords = [],
    operationType,
    insert_above_chunk_id,
  } = props
  const [form] = Form.useForm()
  const [keywords, setKeyWords] = useState(paragraph.keywords ?? [])

  const [hasError, serHasError] = useState(false)

  const { ossClient } = useOSSClient({ business: 'dataset' })

  const { runAsync, loading } = useRequest(updateSplitContent, { manual: true })
  const { runAsync: runAddSplitItem, loading: addLoading } = useRequest(
    addSplitContent,
    { manual: true },
  )

  const addAnswer = () => {
    const { extra_info } = form.getFieldsValue()
    let questionTitle = `答案${extra_info.length + 1}`
    // 检查名字是否重复，如果重复则尝试新的名字
    while (extra_info.find((item: any) => item.name === questionTitle)) {
      questionTitle = `${questionTitle}~`
    }
    if (extra_info.length >= MAX_ANSWER_COUNT) {
      return
    }

    extra_info.forEach((o: any) => {
      if (questionTitle === o.title) {
        questionTitle = `${questionTitle}~`
      }
    })
    extra_info.push({
      title: questionTitle,
      value: '',
      id: uuidv4(),
    })
    form.setFieldValue('extra_info', [...extra_info])
  }

  const extraAnswerLength = Form.useWatch('extra_info', form)

  const formatExtraInfo = useMemo(() => {
    const keys = Object.keys(paragraph?.extra_info || {})
    return keys.map(item => {
      return {
        title: item,
        value: paragraph?.extra_info?.[item] || '',
        id: uuidv4(),
      }
    })
  }, [paragraph?.extra_info])

  const onOk = async () => {
    await form.validateFields()
    const extra_info = form.getFieldValue('extra_info')
    const extraInfoData: any = {}
    extra_info.forEach((item: any) => {
      extraInfoData[item.title] = item.value
    })
    const baseParams = {
      file_id,
      ...form.getFieldsValue(),
      extra_info: extraInfoData,
      keywords,
    }

    if (operationType === 'update') {
      const updateParams = {
        chunk_id,
        enable: paragraph.enable,
      }
      await runAsync({ ...baseParams, ...updateParams })
      callback?.({ ...baseParams, ...updateParams })
    } else {
      const addParams = {
        enable: true,
        insert_above_chunk_id,
      }
      const res = await runAddSplitItem({ ...baseParams, ...addParams })
      callback?.({ ...res, key: res.chunk_id })
    }
    message.success(operationType === 'update' ? '修改已保存' : '添加成功')
    onModalClose?.()
  }

  return (
    <Modal
      title={title}
      open={open}
      maskClosable={false}
      onCancel={onModalClose}
      footer={null}
      width={1020}
      styles={{
        body: {
          minHeight: '420px',
          height: '67vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
        },
      }}
    >
      <div
        className={classNames('px-32 py-24', {
          '!pt-0': operationType !== 'update',
        })}
      >
        {operationType === 'update' && (
          <div className='flex flex-wrap gap-8'>
            <KeywordsEditor
              defaultValue={keywords}
              readOnly={false}
              highlightKeywords={matched_keywords}
              onChange={setKeyWords}
            />
          </div>
        )}
        <div
          className={classNames('pt-[24px] pb-[24px]', {
            '!pt-8': operationType !== 'update',
          })}
        >
          <FormWrapper
            form={form}
            layout={'vertical'}
            initialValues={{
              content: paragraph?.content || '',
              extra_info: formatExtraInfo,
            }}
          >
            <Form.Item
              label={
                <div className='flex items-center'>
                  <span
                    className={classNames(
                      'bg-[rgba(123,97,255,0.12)] c-[#7B61FF] text-[12px] line-height-14px font-medium p-[4px] rounded-[4px] font-medium',
                    )}
                  >
                    问
                  </span>
                  <span className='ml-[12px] font-medium'>问题</span>
                  <RequireIcon />
                </div>
              }
              name='content'
              rules={[
                { required: true, message: '请输入问题' },
                {
                  max: MAX_QUESTION_CONTENT_COUNT,
                  message: `输入错误，最多${MAX_QUESTION_CONTENT_COUNT}个字符`,
                },
              ]}
            >
              <QuestionItem readOnly={false} max={MAX_QUESTION_CONTENT_COUNT} />
            </Form.Item>

            <Form.Item
              name='extra_info'
              label={
                <div className='flex items-center'>
                  <span className='bg-[rgba(56,166,255,0.12)] c-[#38A6FF] text-[12px] line-height-14px font-medium p-[4px] rounded-[4px]'>
                    答
                  </span>
                  <span className='ml-[12px] font-medium'>答案</span>
                  <RequireIcon />
                </div>
              }
              rules={[
                { required: true, message: '请输入答案' },
                {
                  validator(_rule, value, callback) {
                    const res = checkArrayItems(value)
                    if (!res.success) {
                      callback(res.message)
                    }
                    if (value.length > MAX_ANSWER_COUNT) {
                      callback(`答案数量最多为${MAX_ANSWER_COUNT}个`)
                    }
                    if (!value.length) {
                      callback('答案不能为空')
                    }
                    serHasError(
                      value.length > MAX_ANSWER_COUNT ||
                        !res.success ||
                        !value.length,
                    )
                    callback()
                  },
                },
              ]}
            >
              <AnswerEditor
                readOnly={false}
                ossClient={ossClient}
                file_id={file_id}
              />
            </Form.Item>
            <Form.Item noStyle>
              <Button
                icon={<IconFont name='tianjiahuihua' className='text-14px' />}
                className={classNames('relative mt-12px', {
                  'top-[-28px] ': !hasError,
                })}
                // className='relative'
                disabled={extraAnswerLength?.length >= MAX_ANSWER_COUNT}
                onClick={() => {
                  addAnswer()
                }}
              >
                添加答案
              </Button>
            </Form.Item>
          </FormWrapper>
        </div>
      </div>
      <div className='px-32 pb-24 flex items-center justify-end gap-12 absolute bottom-0 w-full left-0 bg-white rounded-[0_0_8px_8px] z-10'>
        <>
          <Button
            onClick={() => {
              form.resetFields()
              onModalClose?.()
            }}
          >
            取消
          </Button>
          <Button
            loading={operationType === 'update' ? loading : addLoading}
            type='primary'
            onClick={onOk}
          >
            保存
          </Button>
        </>
      </div>
    </Modal>
  )
}
